import React, { useState, useRef, useEffect } from 'react';
import Webcam from 'react-webcam';
import { v4 as uuidv4 } from 'uuid';
import './App.css';

function App() {
  const webcamRef = useRef(null);

  const viewsConfig = [
    {
      images: [
        "/assets/View_1_Open_Unselect.png",
        "/assets/View_1_Capture.png",
        "/assets/View 1_Confirmed.png",
        "/assets/View1_Reshoot.png"
      ],
      outline: "/assets/Outline_1.png",
      setIndex: useState(0),
      setBlob: useState(null),
      setShowTick: useState(false),
      setLoading: useState(false)
    },
    {
      images: [
        "/assets/View_2_Open_Unselect.png",
        "/assets/View_2_Capture.png",
        "/assets/View 2_Confirmed.png",
        "/assets/View2_Reshoot.png"
      ],
      outline: "/assets/Outline_2.png",
      setIndex: useState(0),
      setBlob: useState(null),
      setShowTick: useState(false),
      setLoading: useState(false)
    }
  ];

  const [screenShotImage] = useState("/assets/Sreenshot1.png");
  const [progressPercentage] = useState(77);
  const [activeView, setActiveView] = useState(null);
  const [lastSelectedView, setLastSelectedView] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // useEffect(() => {
  //   const handleResize = () => setIsMobile(window.innerWidth <= 768);
  //   window.addEventListener('resize', handleResize);
  //   return () => window.removeEventListener('resize', handleResize);
  // }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);

      // Dynamically set the viewport height CSS variable
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    handleResize(); // Run once on mount
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);


  const captureImageBlob = async (videoEl) => {
    const canvas = document.createElement('canvas');
    canvas.width = videoEl.videoWidth;
    canvas.height = videoEl.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(videoEl, 0, 0, canvas.width, canvas.height);
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg'));
  };

  const handleNext = () => {
    const capturedCount = viewsConfig.filter(view => view.setBlob[0]).length;
    setCurrentPreviewIndex((prev) => (prev + 1) % capturedCount);
  };

  const handlePrev = () => {
    const capturedCount = viewsConfig.filter(view => view.setBlob[0]).length;
    setCurrentPreviewIndex((prev) => (prev === 0 ? capturedCount - 1 : prev - 1));
  };

  const handlePreviewClick = () => {
    const captured = viewsConfig.map(view => view.setBlob[0]).filter(Boolean);
    if (captured.length === 0) return alert("No captured images to preview!");
    setCurrentPreviewIndex(0);
    setIsPreviewMode(true);
  };

  const handleViewClick = async (viewIndex) => {
    const [index, setIndex] = viewsConfig[viewIndex].setIndex;
    const [, setBlob] = viewsConfig[viewIndex].setBlob;
    const [, setShowTick] = viewsConfig[viewIndex].setShowTick;
    const [, setLoading] = viewsConfig[viewIndex].setLoading;

    if (activeView !== null && activeView !== viewIndex) return;
    setActiveView(viewIndex);
    setLastSelectedView(viewIndex);

    if (index === 0) {
      setIndex(1);
    } else if (index === 1 || index === 3) {
      setLoading(true);
      setShowTick(true);
      const blob = await captureImageBlob(webcamRef.current.video);
      setBlob(blob);
      setIndex(2);
      setTimeout(() => {
        setLoading(false);
        setShowTick(false);
        setActiveView(null);
      }, 2000);
    } else if (index === 2) {
      setIndex(3);
    }
  };

  const handleUploadClick = async () => {
    let hasBlob = viewsConfig.some(view => view.setBlob[0]);
    if (!hasBlob) return alert("No images to upload!");

    setIsUploading(true);
    setUploadProgress(0);

    const uploadSingle = async (blob, label, index) => {
      const filename = `${label}_${uuidv4()}.png`;
      const formData = new FormData();
      formData.append('file', blob, filename);
      const res = await fetch('https://testapi4.kaalo.com/upload', {
        method: 'POST',
        body: formData
      });
      if (!res.ok) throw new Error(`${label} upload failed`);
      setUploadProgress(Math.round(((index + 1) / viewsConfig.filter(v => v.setBlob[0]).length) * 100));
      return res.json();
    };

    try {
      const blobsToUpload = viewsConfig.map((view, i) => [view.setBlob[0], i]).filter(([blob]) => blob);
      for (const [blob, i] of blobsToUpload) {
        await uploadSingle(blob, `view${i + 1}`, i);
        viewsConfig[i].setBlob[1](null);
        viewsConfig[i].setIndex[1](0);
      }
      alert("✅ Images uploaded successfully!");
    } catch (err) {
      alert("Upload failed! " + err.message);
    } finally {
      setIsUploading(false);
    }
  };

  const getInstructionText = () => {
    const allReady = viewsConfig.every(view => view.setIndex[0] === 2);
    if (allReady) return <p>Ready for upload.</p>;

    let viewToUse = activeView ?? lastSelectedView ?? viewsConfig.findIndex(view => view.setIndex[0] === 0);
    if (viewToUse === -1 || viewToUse === undefined) return null;

    const [index] = viewsConfig[viewToUse].setIndex;

    if (index === 0) {
      return (
        <>
          <p>Tap ‘View’ to align the position.</p>
          <p>Tap ‘Preview’ to review images.</p>
          <p>Tap ‘Upload’ when ready.</p>
        </>
      );
    } else if (index === 1) {
      return <p>Tap ‘Capture’ to store the image once aligned.</p>;
    } else if (index === 3) {
      return <p>Tap ‘Reshoot’ to capture the image again.</p>;
    } else if (index === 2) {
      return (
        <>
          <p>Tap ‘View’ to align the position.</p>
          <p>Tap ‘Preview’ to review images.</p>
          <p>Tap ‘Upload’ when ready.</p>
        </>
      );
    }

    return null;
  };

  return (
    <div className="app-container">
      {isPreviewMode && (
        <div className="preview-overlay">
          <button className="close-preview-btn" onClick={() => setIsPreviewMode(false)}>✕</button>
          <div className="previewImg">
            <img src="assets/Preview_images.png" className="whiteTick" alt="Preview Label" />
            <div className="imgAddTxt">Preview images</div>
            {(() => {
              const previewBlobs = viewsConfig.map(view => view.setBlob[0]).filter(Boolean);
              const previewURL = previewBlobs[currentPreviewIndex]
                ? URL.createObjectURL(previewBlobs[currentPreviewIndex])
                : screenShotImage;
              return (
                <>
                  {/* <img src="/assets/Mask_Group_2.png" alt="mask_group" className="top-right mask_group" /> */}
                  <img src={previewURL} className="screenShotImage" alt={`Preview ${currentPreviewIndex + 1}`} />
                  <img src="assets/Left_arrow.png" className="leftArrow" alt="Left" onClick={handlePrev} />
                  <img src="assets/Right_arrow.png" className="rightArrow" alt="Right" onClick={handleNext} />
                  <div className="pageNo">
                    {currentPreviewIndex + 1} of {previewBlobs.length}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {isUploading && (
        <div className="uploading-overlay">
          <div className="circular-progress">
            <svg width="120" height="120">
              <circle className="bg" cx="60" cy="60" r="54" />
              <circle
                className="progress"
                cx="60"
                cy="60"
                r="54"
                style={{ strokeDashoffset: `calc(339.292 - (339.292 * ${uploadProgress} / 100))` }}
              />
            </svg>
            <div className="label">
              <div className="status">Uploading...</div>
              <div className="count">{uploadProgress}%</div>
            </div>
          </div>
        </div>
      )}

      <Webcam
        audio={false}
        ref={webcamRef}
        screenshotFormat="image/jpeg"
        screenshotQuality={1}
        className="fullscreen-webcam"
        videoConstraints={{
          facingMode: { ideal: isMobile ? "environment" : "user" },
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }}
      />

      {!isPreviewMode && !isUploading && (
        <>
          <div className="main-content">
            <img src="/assets/DARA.png" alt="DARA" className="top-left DARA" />

            {activeView !== null && !viewsConfig.some(view => view.setShowTick[0]) && (
              <img
                src={viewsConfig[activeView].outline}
                alt="Outline"
                className="centered-screen-image"
              />
            )}

            {/* {viewsConfig.map((view, i) => {
              const [showTick] = view.setShowTick;
              const [blob] = view.setBlob;
              const previewURL = blob ? URL.createObjectURL(blob) : screenShotImage;
              return showTick && (
                <div className="preview-overlay">
                  <div key={i} className="imageAdded">
                    <img src="assets/whiteTick.png" className="whiteTick" alt="tick" />
                    <div className="imgAddTxt">Image Added!</div>
                    <img src={previewURL} className="screenShotImage" alt={`Screenshot ${i + 1}`} />
                  </div>
                </div>
              );
            })} */}

            {viewsConfig.map((view, i) => {
              const [showTick] = view.setShowTick;
              const [blob] = view.setBlob;

              if (!showTick || !blob) return null; // 💡 Prevents showing overlay prematurely

              const previewURL = URL.createObjectURL(blob);
              return (
                <div key={i} className="preview-overlay">
                  <div className="imageAdded">
                    <img src="assets/whiteTick.png" className="whiteTick" alt="tick" />
                    <div className="imgAddTxt">Image Added!</div>
                    <img src={previewURL} className="screenShotImage" alt={`Screenshot ${i + 1}`} />
                  </div>
                </div>
              );
            })}


            {/* <div className="circular-progress previewImg">
              <svg width="120" height="120">
                <circle className="bg" cx="60" cy="60" r="54" />
                <circle
                  className="progress"
                  cx="60" cy="60" r="54"
                  style={{ strokeDashoffset: `calc(339.292 - (339.292 * ${progressPercentage} / 100))` }}
                />
              </svg>
              <div className="label">
                <div className="status">In Progress</div>
                <div className="count">1 of 1</div>
              </div>
            </div> */}
          </div>

          <div className="footer" style={{ backgroundImage: `url('/assets/Blue_box.png')` }}>
            <div className="image-overlay">
              {viewsConfig.map((view, i) => {
                const [index] = view.setIndex;
                return (
                  <img
                    key={i}
                    src={view.images[index]}
                    alt={`View ${i + 1}`}
                    className="overlay-img"
                    onClick={() => handleViewClick(i)}
                    style={{ cursor: activeView !== null && activeView !== i ? "not-allowed" : "pointer" }}
                  />
                );
              })}
            </div>

            <div className="upload-container">
              <div className='Preview_button'>
                <img className='Upload_button' src="/assets/Preview_button.png" alt="Preview Button" onClick={handlePreviewClick} />
                <img className='Upload_button' src="/assets/Upload_button.png" alt="Upload Button" onClick={handleUploadClick} />
              </div>
              <div className="viewText">
                {getInstructionText()}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default App;
